import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  INTAKE_CHANGE_PATH_HELP,
  INTAKE_CHANGE_PATH_LABELS,
  type IntakeEvaluationPayload,
} from "../../utils/intakeEvaluation";

interface IntakeEvaluationPanelProps {
  evaluation: IntakeEvaluationPayload | null;
  saving?: boolean;
  saveError?: string | null;
  savedUuid?: string | null;
  onSave?: () => void;
}

const coverageColor = (
  band: string,
): "success" | "warning" | "error" | "default" => {
  if (band === "full") return "success";
  if (band === "partial") return "warning";
  if (band === "none") return "error";
  return "default";
};

const capabilityColor = (
  status: string,
): "success" | "warning" | "error" | "default" => {
  if (status === "covered") return "success";
  if (status === "partial") return "warning";
  if (status === "missing") return "error";
  return "default";
};

const coverageLabel = (band: string): string => {
  if (band === "full") return "Full coverage";
  if (band === "partial") return "Partial coverage";
  if (band === "none") return "No coverage";
  return band;
};

const IntakeEvaluationPanel: React.FC<IntakeEvaluationPanelProps> = ({
  evaluation,
  saving = false,
  saveError = null,
  savedUuid = null,
  onSave,
}) => {
  if (!evaluation) {
    return (
      <Paper variant="outlined" sx={{ p: 2, minHeight: 160 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Catalog score
        </Typography>
        <Typography variant="body2" color="text.secondary">
          After the assessment finishes, coverage %, capability gaps, and the recommended
          change path appear here.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
        <Typography variant="subtitle2">Catalog score</Typography>
        <Chip
          size="small"
          label={evaluation.satisfied ? "Satisfied" : "Not satisfied"}
          color={evaluation.satisfied ? "success" : "warning"}
        />
        <Chip
          size="small"
          label={`${coverageLabel(evaluation.coverage)} · ${evaluation.coveragePercent}%`}
          color={coverageColor(evaluation.coverage)}
        />
        <Chip
          size="small"
          label={INTAKE_CHANGE_PATH_LABELS[evaluation.recommendedChangePath]}
          variant="outlined"
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {INTAKE_CHANGE_PATH_HELP[evaluation.recommendedChangePath]}
      </Typography>
      {evaluation.requirementSummary && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {evaluation.requirementSummary}
        </Typography>
      )}
      {evaluation.rationale && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {evaluation.rationale}
        </Typography>
      )}
      {evaluation.capabilities.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Capability</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Operations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {evaluation.capabilities.map((cap, i) => (
              <TableRow key={cap.id ?? `${cap.name}-${i}`}>
                <TableCell>{cap.name}</TableCell>
                <TableCell>
                  <Chip size="small" label={cap.status} color={capabilityColor(cap.status)} />
                </TableCell>
                <TableCell>
                  {(cap.matchedOperations ?? []).join(", ") || cap.notes || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {evaluation.matchedApis.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Matched APIs
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {evaluation.matchedApis.map((api) => (
              <Typography key={api.uuid} variant="body2">
                <Link component={RouterLink} to={`/api-explorer/${api.uuid}`}>
                  {api.name ?? api.uuid}
                </Link>
                {api.version ? ` ${api.version}` : ""} · {api.role ?? "supporting"} ·{" "}
                {api.coveragePercent ?? 0}%
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
      {evaluation.gaps && evaluation.gaps.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Gaps and assumptions
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5, pl: 2 }} component="ul">
            {evaluation.gaps.map((gap, i) => (
              <Typography key={`${gap}-${i}`} variant="body2" component="li">
                {gap}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
      {evaluation.recommendedChangePath === "extend-existing" &&
        evaluation.recommendedApiUuid && (
          <Button
            component={RouterLink}
            to={`/agent-dashboard/architect?tab=enhance&api=${evaluation.recommendedApiUuid}`}
            size="small"
            sx={{ mr: 1, textTransform: "none" }}
          >
            Open Architect Enhance
          </Button>
        )}
      {evaluation.recommendedChangePath === "new-api" && (
        <Button
          component={RouterLink}
          to="/agent-dashboard/architect?tab=build"
          size="small"
          sx={{ mr: 1, textTransform: "none" }}
        >
          Open Architect Build
        </Button>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {saveError}
        </Alert>
      )}
      {savedUuid && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Saved. Reopen it anytime from Past assessments.
        </Alert>
      )}
      {onSave && (
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={saving}
          sx={{ mt: 2, textTransform: "none" }}
        >
          {saving ? "Saving…" : savedUuid ? "Update in CMS" : "Save to CMS"}
        </Button>
      )}
    </Paper>
  );
};

export default IntakeEvaluationPanel;
